import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, input, model, output } from '@angular/core';

import { TranslatePipe } from '@ngx-translate/core';

import type { DropdownOption } from '@valk-nx/components/ui-select-shell/src/lib/select-shell.interface';
import { TagComponent } from '@valk-nx/components/ui-tag/src/lib/tag';
import { FilterMapButtonComponent } from '@valk-nx/compositions/ui-filters/src/lib/components/filter-map-button/filter-map-button';
import { SVG_ICON_IMPORTS } from '@valk-nx/helpers/lib/svg-unique-ids/svg-unique-ids.directive';

import { FilterCheckboxGroupComponent } from './components/filter-checkbox-group/filter-checkbox-group';
import { FilterLayoutComponent } from './components/filter-layout/filter-layout';
import { FilterNumericComponent } from './components/filter-numeric/filter-numeric';
import { FilterSearchComponent } from './components/filter-search/filter-search';
import type { FiltersInterface } from './contracts/filters.interface';
import { SUPPORTED_FILTER_TYPES } from './contracts/filters.interface';

interface FilterChipsInterface {
	group: string;
	name: string;
	value: string;
	type?: FiltersInterface['type'];
}

/** Shared by every filter group type that collapses long item lists. */
export const ITEMS_LIMIT = 8;

/**
 * Chip group of the searched location. Not a key of `activeFilters`: the
 * location is request state the parent owns, so removing its chip is
 * reported through `searchCleared` instead of `activeFiltersChange`.
 */
export const SEARCH_LOCATION_GROUP = 'search-location';

@Component({
	selector: `vp-filters`,
	templateUrl: './filters.component.html',
	imports: [
		FilterCheckboxGroupComponent,
		FilterLayoutComponent,
		FilterNumericComponent,
		FilterSearchComponent,
		NgTemplateOutlet,
		TagComponent,
		TranslatePipe,
		SVG_ICON_IMPORTS,
		FilterMapButtonComponent,
	],
})
export class FiltersComponent {
	protected readonly ITEMS_LIMIT = ITEMS_LIMIT;

	filters = input<FiltersInterface[]>([]);
	searchOptions = input<DropdownOption[]>([]);
	searchLoading = input<boolean>(false);
	searchLocation = input<string | null>(null);
	showMapButton = input(true);

	activeFilters = model<Record<string, string[]>>({});

	searched = output<string>();
	searchSelected = output<string>();
	searchCleared = output<void>();
	showMap = output<void>();

	activeFilterChips = computed<FilterChipsInterface[]>(() => {
		const active = this.activeFilters();
		const groups = this.filters();
		const location = this.searchLocation();

		const locationChips: FilterChipsInterface[] = location
			? [
					{
						group: SEARCH_LOCATION_GROUP,
						name: location,
						value: location,
					},
				]
			: [];

		const filterChips = Object.entries(active).flatMap(
			([groupTitle, values]) => {
				const group = groups.find((g) => g.title === groupTitle);
				const items = group?.items ?? [];

				return values.map((value) => ({
					group: groupTitle,
					name: items.find((i) => i.value === value)?.name ?? value,
					value,
					type: group?.type,
				}));
			},
		);

		return [...locationChips, ...filterChips];
	});

	filteredFilters = computed<FiltersInterface[]>(() => {
		return this.filters().filter((f) =>
			SUPPORTED_FILTER_TYPES.includes(f.type),
		);
	});

	numericValues = computed<Record<string, string>>(() => {
		const active = this.activeFilters();

		return Object.fromEntries(
			this.filteredFilters()
				.filter((group) => group.type === 'numeric')
				.map((group) => [group.title, active[group.title]?.[0] ?? '']),
		);
	});

	onSearched(term: string): void {
		this.searched.emit(term);
	}

	onSearchSelected(mapboxId: string): void {
		this.searchSelected.emit(mapboxId);
	}

	clearAllFilters(): void {
		this.activeFilters.set({});
		if (this.searchLocation()) {
			this.searchCleared.emit();
		}
	}

	removeFilter(groupTitle: string, value: string): void {
		if (groupTitle === SEARCH_LOCATION_GROUP) {
			this.searchCleared.emit();
			return;
		}

		const groupValues = this.activeFilters()[groupTitle] ?? [];

		this.setGroupValues(
			groupTitle,
			groupValues.filter((v) => v !== value),
		);
	}

	setNumericValue(groupTitle: string, value: string): void {
		this.setGroupValues(groupTitle, value ? [value] : []);
	}

	setGroupValues(groupTitle: string, values: string[]): void {
		this.activeFilters.update((current) => {
			const next = { ...current };

			if (values.length > 0) {
				next[groupTitle] = values;
			} else {
				delete next[groupTitle];
			}

			return next;
		});
	}
}
